import { useEffect, useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import { SortableArea } from "@/components/admin/Sortable";
import { UploadBtn } from "@/components/admin/SermonForm";
import { ColsPicker, Inline } from "@/components/admin/ve/VideosFields";
import { fileUrl } from "@/lib/api";

const Item = ({ p, i, t, onEdit, onRemove, isCover, onCover }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={`${t}-photo-${i}`} className={`flex items-center gap-2 rounded-lg border bg-white p-2 ${isDragging ? "z-10 border-[#C8862F] shadow-lg" : "border-[#14213D]/10"}`}>
      <button type="button" data-testid={`${t}-photo-drag-${i}`} {...attributes} {...listeners} aria-label="Drag to reorder" className="cursor-grab touch-none rounded p-0.5 text-[#5F6875] active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
      <img src={fileUrl(p.url)} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />
      <div className="min-w-0 flex-1"><Inline testid={`${t}-photo-title-${i}`} value={p.title} placeholder="Caption (optional)" onSave={(x) => onEdit({ ...p, title: x })} /></div>
      {onCover && (isCover
        ? <span data-testid={`${t}-photo-is-cover-${i}`} className="shrink-0 rounded-full px-2 py-0.5 font-ui text-[10px] font-semibold" style={{ backgroundColor: "#C8862F", color: "#FFFFFF" }}>Cover</span>
        : <button type="button" data-testid={`${t}-photo-set-cover-${i}`} onClick={onCover} className="shrink-0 rounded-full border border-[#C8862F]/50 px-2 py-0.5 font-ui text-[10px] text-[#8A6A12] hover:bg-[#C8862F]/10">Set as cover</button>)}
      <button type="button" data-testid={`${t}-photo-remove-${i}`} onClick={onRemove} aria-label="Remove photo" className="rounded-full p-1 text-[#B4442A] hover:bg-[#B4442A]/10"><X className="h-3.5 w-3.5" /></button>
    </li>
  );
};

// Photo gallery: upload many photos at once, caption them, drag to reorder, and choose 4/6/8 across.
export const PhotosFields = ({ b, onChange, noCols, cover, onCover }) => {
  const latest = useRef(b);
  latest.current = b;
  const items = Array.isArray(b.items) ? b.items : [];
  const live = useRef(items);
  useEffect(() => { live.current = Array.isArray(b.items) ? b.items : []; }, [b.items]);
  const save = (list, extra = {}) => {
    live.current = list;
    onChange({ ...latest.current, items: list, ...extra });
  };
  const t = `ve-block-${b.id}`;
  return (
    <div className="grid gap-3">
      {!noCols && <ColsPicker value={b.cols} onPick={(n) => save(live.current, { cols: n })} t={t} noun="Photos" />}
      <UploadBtn accept="image/*" multiple testid={`${t}-photos-upload`} label={items.length ? "Add more photos" : "Upload photos (pick several)"}
        onDone={(url) => save([...live.current, { id: `p-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, url, title: "" }])} />
      {items.length > 0 && <p className="font-ui text-[11px] text-[#5F6875]">{items.length} photo(s). Drag the handles to change the order.</p>}
      <SortableArea ids={items.map((p) => p.id)} onReorder={(ids) => save(ids.map((id) => items.find((p) => p.id === id)))}>
        <ul className="grid gap-2">
          {items.map((p, i) => (
            <Item key={p.id} p={p} i={i} t={t} isCover={onCover && (cover ? cover === p.url : i === 0)} onCover={onCover && (() => onCover(p.url))}
              onEdit={(np) => save(live.current.map((x) => (x.id === p.id ? np : x)))} onRemove={() => save(live.current.filter((x) => x.id !== p.id))} />
          ))}
        </ul>
      </SortableArea>
    </div>
  );
};
